import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchData } from "../api/client";
import { getActiveWorkspaceSlug } from "../utils/workspaceStorage";
import { DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY } from "./useDashboardFinanceAttendanceReadiness";
import { useAuthContext } from "../context/AuthContext";

/**
 * Lists users.
 * - default behavior keeps existing screens stable: `tipo_responsavel=humanos`
 * - pass `tipoResponsavel: "todos"` (or falsy) to omit the backend filter and include ChatBots (ura=1)
 * - pass `order: "nome"` for assignee/filter selects (stable list under LIMIT).
 *   Chat transfer/assignee UIs should use limit 1000 + order nome so colleagues
 *   are not dropped by recency (`data_ultima_alteracao DESC`).
 */
export const useUsers = (page = 1, limit = 100, search = "", tipoResponsavel = "humanos", options = {}) => {
  const { enabled = true, order = "" } = options;
  const queryClient = useQueryClient();
  const tenantSlug = getActiveWorkspaceSlug();
  const { bearerToken } = useAuthContext() || {};
  const canFetch = Boolean(tenantSlug && bearerToken) && enabled;

  const query = useQuery(
    ["users", page, limit, search, tipoResponsavel, order || "", tenantSlug || ""],
    () =>
      (() => {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
          search: search || "",
        });
        if (tipoResponsavel && tipoResponsavel !== "todos") {
          params.set("tipo_responsavel", tipoResponsavel);
        }
        if (order) params.set("order", order);
        return fetchData(`/users?${params.toString()}`);
      })(),
    {
      enabled: canFetch,
      keepPreviousData: true,
      staleTime: 5000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = error?.response?.status ?? error?.status;
        const code = error?.response?.data?.code ?? error?.code;
        const transient =
          status === 503 ||
          code === "errors.auth.temporarily_unavailable" ||
          code === "errors.auth.forbidden";
        if (!transient) return failureCount < 1;
        return failureCount < 3;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
      select: (res) => ({
        users: res.data || [],
        pagination: res.pagination || { total: 0 },
      }),
    }
  );

  const updateMutation = useMutation(
    (payload) => {
      const method = payload.id ? "PUT" : "POST";
      const endpoint = payload.id ? `/users/${payload.id}` : `/users`;
      return fetchData(endpoint, method, payload);
    },
    { onSuccess: () => {
      queryClient.invalidateQueries("users");
      queryClient.invalidateQueries(DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY);
    } }
  );

  const deleteMutation = useMutation(
    (payload) => {
      const id = payload && typeof payload === "object" ? payload.id : payload;
      const resolutions = payload && typeof payload === "object" ? payload.resolutions : undefined;
      return fetchData(`/users/${id}`, "DELETE", resolutions ? { resolutions } : {});
    },
    { onSuccess: () => {
      queryClient.invalidateQueries("users");
      queryClient.invalidateQueries(DASHBOARD_FINANCE_ATTENDANCE_READINESS_KEY);
    } }
  );

  return { ...query, updateMutation, deleteMutation };
};
